// Type definitions for WizeCom Content Planner

export type AppRole = 'admin' | 'editor' | 'viewer';
export type SlotType = 'social';
export type ChannelType = 'linkedin' | 'facebook' | 'instagram';
export type SlotStatus = 'planned' | 'drafted' | 'reviewed' | 'scheduled' | 'published' | 'skipped';
export type ContentStatus = 'draft' | 'reviewed' | 'scheduled' | 'published';
export type IntegrationProvider = 'gemini' | 'meta' | 'linkedin';
export type IntegrationStatus = 'disconnected' | 'connected' | 'error';
export type Seasonality = 'winter' | 'spring' | 'summer' | 'autumn' | 'always';
export type PriorityLevel = 'low' | 'normal' | 'high';
export type PublishProvider = 'meta' | 'linkedin' | 'manual';
export type JobStatus = 'queued' | 'processing' | 'success' | 'failed';
export type AssetType = 'image' | 'document';

export interface ChannelScheduleEntry {
  posts_per_week: number;
  days: string[];
}

export type ChannelSchedule = Partial<Record<ChannelType, ChannelScheduleEntry>>;

// Structured profile types
export interface TargetAudience {
  primary: string;
  segments: string[];
  pain_points: string[];
  decision_drivers?: string[];
}

export interface ToneOfVoice {
  description: string;
  formality: 'formal' | 'semi-formal' | 'casual';
  personality_traits: string[];
  avoids?: string;
}

export interface ContentSignals {
  has_blog?: boolean;
  social_media_links?: string[];
  key_messages?: string[];
  frequently_used_words?: string[];
}

// Helper to extract target_audience as structured object (backward compat)
export function parseTargetAudience(val: unknown): TargetAudience | null {
  if (!val) return null;
  if (typeof val === 'string') {
    return { primary: val, segments: [], pain_points: [] };
  }
  if (typeof val === 'object' && val !== null && 'primary' in val) {
    const obj = val as Record<string, unknown>;
    return {
      primary: (obj.primary as string) || '',
      segments: Array.isArray(obj.segments) ? obj.segments : [],
      pain_points: Array.isArray(obj.pain_points) ? obj.pain_points : [],
      decision_drivers: Array.isArray(obj.decision_drivers) ? obj.decision_drivers : undefined,
    };
  }
  return null;
}

// Helper to extract tone_of_voice as structured object (backward compat)
export function parseToneOfVoice(val: unknown): ToneOfVoice | null {
  if (!val) return null;
  if (typeof val === 'string') {
    return { description: val, formality: 'semi-formal', personality_traits: [] };
  }
  if (typeof val === 'object' && val !== null && 'description' in val) {
    const obj = val as Record<string, unknown>;
    return {
      description: (obj.description as string) || '',
      formality: (['formal', 'semi-formal', 'casual'].includes(obj.formality as string) ? obj.formality : 'semi-formal') as ToneOfVoice['formality'],
      personality_traits: Array.isArray(obj.personality_traits) ? obj.personality_traits : [],
      avoids: (obj.avoids as string) || undefined,
    };
  }
  return null;
}

// Helper to flatten target_audience for prompts
export function targetAudienceToString(val: unknown): string {
  const parsed = parseTargetAudience(val);
  if (!parsed) return '';
  const parts = [parsed.primary];
  if (parsed.segments.length) parts.push(`Segment: ${parsed.segments.join(', ')}`);
  if (parsed.pain_points.length) parts.push(`Smärtpunkter: ${parsed.pain_points.join(', ')}`);
  if (parsed.decision_drivers?.length) parts.push(`Köpbeslutsdrivare: ${parsed.decision_drivers.join(', ')}`);
  return parts.join('. ');
}

// Helper to flatten tone_of_voice for prompts
export function toneOfVoiceToString(val: unknown): string {
  const parsed = parseToneOfVoice(val);
  if (!parsed) return '';
  const parts = [parsed.description];
  if (parsed.formality) parts.push(`Formalitet: ${parsed.formality}`);
  if (parsed.personality_traits.length) parts.push(`Personlighet: ${parsed.personality_traits.join(', ')}`);
  if (parsed.avoids) parts.push(`Undviker: ${parsed.avoids}`);
  return parts.join('. ');
}

export interface Org {
  id: string;
  name: string;
  created_at: string;
  // Company profile fields
  website_url?: string | null;
  description?: string | null;
  products_services?: Array<{ name: string; description?: string }> | null;
  target_audience?: TargetAudience | null;
  tone_of_voice?: ToneOfVoice | null;
  industry_areas?: string[] | null;
  competitors?: string[] | null;
  scraped_at?: string | null;
  // New profile fields
  unique_selling_points?: string[] | null;
  seasonality_description?: string | null;
  geographic_focus?: string | null;
  content_signals?: ContentSignals | null;
  // Channel settings
  enabled_channels?: ChannelType[] | null;
  channel_schedule?: ChannelSchedule | null;
  // Journey / phase tracking (Block A.1)
  journey_started_at?: string | null;
  current_phase?: 'etablera' | 'fordjupa' | 'forfina' | null;
  phase_updated_at?: string | null;
  // Approval timestamps
  profile_approved_at?: string | null;
  strategy_approved_at?: string | null;
  topics_approved_at?: string | null;
  // Carousel / brand settings
  brand_font?: string | null;
  logo_url?: string | null;
  carousel_template?: string | null;
}

export interface Profile {
  id: string;
  full_name: string | null;
  created_at: string;
}

export interface Membership {
  id: string;
  org_id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
}

export interface OrgInvite {
  id: string;
  org_id: string;
  code: string;
  role: AppRole;
  uses_remaining: number | null;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
}

export interface Integration {
  id: string;
  org_id: string;
  provider: IntegrationProvider;
  status: IntegrationStatus;
  config: Record<string, unknown>;
  created_at: string;
}

export interface Topic {
  id: string;
  org_id: string;
  title: string;
  description: string | null;
  industry_area: string | null;
  seasonality: Seasonality;
  priority: PriorityLevel;
  recurring: boolean;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  channel?: string | null;
  content_type?: string | null;
  content_day_id?: string | null;
  product_references?: string[] | null;
  funnel_stage?: string | null;
  content_pillar?: string | null;
  pain_point_addressed?: string | null;
  hook_suggestion?: string | null;
  brief?: {
    vinkel?: string;
    karnbudskap?: string;
    malsegment?: string;
  } | null;
}

export interface CalendarSlot {
  id: string;
  org_id: string;
  slot_type: SlotType;
  channel: ChannelType;
  year: number;
  week: number | null;
  month: number | null;
  topic_id: string | null;
  status: SlotStatus;
  scheduled_for: string | null;
  notes: string | null;
  created_at: string;
  // Joined data
  topic?: Topic;
}

export interface ContentItem {
  id: string;
  org_id: string;
  slot_id: string | null;
  topic_id: string | null;
  channel: ChannelType;
  headline: string | null;
  body: string | null;
  cta: string | null;
  tone: string;
  ai_generated: boolean;
  human_edited: boolean;
  status: ContentStatus;
  scheduled_for: string | null;
  published_at: string | null;
  created_by: string | null;
  created_at: string;
  // Joined data
  topic?: Topic;
  slot?: CalendarSlot;
}

export interface Asset {
  id: string;
  org_id: string;
  type: AssetType;
  channel: string;
  format: string | null;
  storage_path: string;
  prompt: string | null;
  created_by: string | null;
  created_at: string;
}

export interface ContentAsset {
  id: string;
  org_id: string;
  content_id: string;
  asset_id: string;
}

export interface PublishJob {
  id: string;
  org_id: string;
  content_id: string;
  provider: PublishProvider;
  status: JobStatus;
  run_at: string;
  attempts: number;
  last_error: string | null;
  created_at: string;
}

export interface AiPromptVersion {
  id: string;
  org_id: string;
  name: string;
  version: number;
  system_prompt: string;
  user_prompt_template: string;
  model: string;
  is_active: boolean;
  created_at: string;
}

export interface AiRun {
  id: string;
  org_id: string;
  prompt_id: string | null;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
}

// UI Helper types
export interface SlotWithTopic extends CalendarSlot {
  topic: Topic | null;
}

export interface ContentWithRelations extends ContentItem {
  topic: Topic | null;
  slot: CalendarSlot | null;
  assets: Asset[];
}

// Status display configuration
export const STATUS_CONFIG: Record<SlotStatus, { label: string; color: string }> = {
  planned: { label: 'Planerad', color: 'status-planned' },
  drafted: { label: 'Utkast', color: 'status-drafted' },
  reviewed: { label: 'Granskad', color: 'status-reviewed' },
  scheduled: { label: 'Schemalagd', color: 'status-scheduled' },
  published: { label: 'Publicerad', color: 'status-published' },
  skipped: { label: 'Hoppad över', color: 'status-skipped' },
};

export const CHANNEL_CONFIG: Record<ChannelType, { label: string; color: string; icon: string }> = {
  linkedin: { label: 'LinkedIn', color: 'channel-linkedin', icon: 'Linkedin' },
  facebook: { label: 'Facebook', color: 'channel-facebook', icon: 'Facebook' },
  instagram: { label: 'Instagram', color: 'channel-instagram', icon: 'Instagram' },
};

export const SEASONALITY_CONFIG: Record<Seasonality, { label: string }> = {
  winter: { label: 'Vinter' },
  spring: { label: 'Vår' },
  summer: { label: 'Sommar' },
  autumn: { label: 'Höst' },
  always: { label: 'Alltid' },
};

export const PRIORITY_CONFIG: Record<PriorityLevel, { label: string }> = {
  low: { label: 'Låg' },
  normal: { label: 'Normal' },
  high: { label: 'Hög' },
};

// AI Output types
export interface TopicSuggestion {
  title: string;
  description: string;
  industry_area: string;
  seasonality: Seasonality;
  content_day_name?: string;
  channel?: string;
  content_type?: string;
  product_references?: string[];
  funnel_stage?: string;
  content_pillar?: string;
  pain_point_addressed?: string;
  hook_suggestion?: string;
  brief?: {
    vinkel?: string;
    karnbudskap?: string;
    malsegment?: string;
  };
  /** Inline evergreen classification from bulk generation */
  category?: 'evergreen' | 'seasonal' | 'time_bound';
  classification_confidence?: number;
}

export interface AiDraftOutput {
  headline: string;
  body: string;
  cta: string;
}

// V2 Structure output
export interface AiStructureOutput {
  vald_mall: 'A' | 'B' | 'C';
  motivering: string;
  struktur: Array<{ del: string; innehall: string }>;
}

// V2 QC Assessment
export interface AiQcBedomning {
  syftestrogenhet?: 'uppfyllt' | 'delvis' | 'missat';
  hook_styrka: 'stark' | 'medel' | 'svag';
  malgruppsrelevans: 'hög' | 'medel' | 'låg';
  tonalitetsefterlevnad: 'stämmer' | 'delvis' | 'avviker';
  kanaloptimering: 'optimerad' | 'delvis' | 'ej anpassad';
  cta_styrka: 'stark' | 'medel' | 'svag';
  totalbedomning: 'publicera' | 'förbättra' | 'skriv om';
}

export interface AiQcForslag {
  dimension: string;
  problem: string;
  losning: string;
}

export interface AiQcOutput {
  bedomning?: AiQcBedomning;
  forbattringsforslag?: AiQcForslag[];
  // Legacy fallback
  suggestions?: string[];
  improved: AiDraftOutput;
}
